import { createSlice } from "@reduxjs/toolkit";
import { loadAssets, loadIntroAssets } from "./assetsSlice";

// Import universal initializeGame action
import { initializeGame } from "./gameSettingsSlice";

const loadingProgressSlice = createSlice({
  name: "loadingProgress",
  initialState: {
    status: "idle", // idle, loading, success, error
    loaderComplete: false, // true after loader "out" animation finishes
    loaderFadedOut: false, // true after loader fade-out animation visually completes
    // True once the loader's fade-out is halfway through — i.e. the game
    // underneath is already half-revealed. Distinct from loaderFadedOut
    // (which is the very end): this is the cue for anything that should
    // cross-fade IN as the loader goes OUT, rather than wait for it to
    // finish and leave a visible gap.
    loaderFadeMidpoint: false,
    // The intro popup's own textures/fonts/translations are force-loaded ahead
    // of the main bundle, so the intro can take over from the loader while the
    // rest of the game is still downloading.
    introAssetsReady: false,
    // True once the intro popup should be mounted. Set by the loader after its
    // spine has completed one full revolution AND introAssetsReady is true —
    // i.e. "one revolution or the early bundle, whichever is later".
    introMounted: false,
  },
  reducers: {
    // Both of these mount things — the game tree and the intro popup — and
    // both are guarded on the error state.
    //
    // This is the authoritative stop. A failed init used to be a purely
    // cosmetic event: the error was drawn, and the loader's hand-off carried
    // on regardless, mounting the intro popup over the top of it and letting
    // the rest of the game load behind a dead session. Guarding at the
    // reducer means it does not matter who dispatches, or when, or how many
    // callers there are — after an error nothing mounts.
    setLoaderComplete: (state) => {
      if (state.status === "error") return;
      state.loaderComplete = true;
    },
    setLoaderFadedOut: (state) => {
      state.loaderFadedOut = true;
    },
    setLoaderFadeMidpoint: (state) => {
      state.loaderFadeMidpoint = true;
    },
    setIntroMounted: (state) => {
      if (state.status === "error") return;
      state.introMounted = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAssets.pending, (state) => {
        if (state.status === "idle") {
          state.status = "loading";
        }
      })
      .addCase(loadIntroAssets.fulfilled, (state) => {
        state.introAssetsReady = true;
      })
      // A failed early bundle must not strand the player on the loader — the
      // intro degrades (missing art) rather than never appearing at all.
      .addCase(loadIntroAssets.rejected, (state) => {
        state.introAssetsReady = true;
      })
      .addCase(initializeGame.fulfilled, (state) => {
        state.status = "success";
      })
      // Hard stop. Anything already flagged is un-flagged, which covers the
      // race where init rejects just after the loader handed over: without
      // this the intro popup would stay mounted underneath the error.
      .addCase(initializeGame.rejected, (state) => {
        state.status = "error";
        state.loaderComplete = false;
        state.introMounted = false;
      });
  },
});

export const {
  setLoaderComplete,
  setLoaderFadedOut,
  setLoaderFadeMidpoint,
  setIntroMounted,
} = loadingProgressSlice.actions;

export const selectLoaderFadeMidpoint = (state) =>
  state.loadingProgress.loaderFadeMidpoint;
export const selectIntroAssetsReady = (state) =>
  state.loadingProgress.introAssetsReady;
export const selectIntroMounted = (state) =>
  state.loadingProgress.introMounted;
/** True when init failed — nothing may mount, and the error popup owns the screen. */
export const selectLoadingFailed = (state) =>
  state.loadingProgress.status === "error";
/** True once EVERYTHING is loaded — what unlocks the tap-to-start button. */
export const selectGameFullyLoaded = (state) =>
  state.loadingProgress.status === "success";
export default loadingProgressSlice.reducer;
